import {
    AnswerEntityId,
    COMMUNITY_REFERRAL,
    CommunityId,
    Contact,
    ContactId,
    DefaultReferences,
    IContact,
    Maybe,
    parseEntityId,
    ReferenceBusinessId,
    ReferenceContactId,
    ReferenceMap,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferRouterInputs } from "@trpc/server";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContactCard } from "components/contacts/card";
import { Icon } from "components/icon";
import { DeleteButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import {
    AdvancedSearchEntityType,
    useCreateReferenceBusinessMutation,
    useCreateReferenceContactMutation,
    useGetCommunityQuery,
    useGetReferenceBusinessQuery,
    useGetReferenceContactQuery,
} from "generated/graphql";
import React, { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import {
    useCanSeeReferralSourceOwners,
    useRegionId,
    useUsers,
} from "store/selectors/hooks";
import { NewInlineOrganization } from "./new-inline-organization";
import { NewInlineReference } from "./new-inline-reference";
import { ReferralSourceSearch, ReferralSourceSearchTab } from "./referral-source-search";

export type ReferralSourceMutationInput = NonNullable<
    Exclude<inferRouterInputs<AppRouter>["clients"]["setReferralSource"], void>["source"]
>;

export const toMutationInput = (map: ReferenceMap): ReferralSourceMutationInput => {
    const identifier = map.identifier.getOrElse(null);
    const business = map.business.getOrElse(null);
    const contact = map.contact.getOrElse(null);

    if (business && identifier === COMMUNITY_REFERRAL) {
        return {
            kind: "community",
            communityId: business as unknown as CommunityId,
            contactId: contact as ReferenceContactId | null,
        };
    }
    if (business) {
        return {
            kind: "business",
            businessId: business as ReferenceBusinessId,
            contactId: contact as ReferenceContactId | null,
        };
    }
    if (contact) {
        return {
            kind: "contact",
            contactId: contact as ReferenceContactId,
        };
    }
    if (identifier) {
        return { kind: "token", token: identifier };
    }
    return { kind: "none" };
};

interface AssignedToProps {
    userId: UserId;
}

const AssignedTo: React.FC<AssignedToProps> = props => {
    const { userId } = props;

    const userState = useUsers();
    const isReferralPreferenceEnabled = useCanSeeReferralSourceOwners();

    if (!isReferralPreferenceEnabled || !userId || !userState.users[userId]) {
        return null;
    }

    return <p className="text-xs italic">Assigned to: {userState.users[userId].name}</p>;
};

interface ReferenceContactValueProps {
    referenceContactId: ReferenceContactId;
    referenceBusinessId: ReferenceBusinessId | null;
    communityId: CommunityId | null;
    parentEntityType: AdvancedSearchEntityType;
    parentId: AnswerEntityId;
}

const wrapperStyle = "";

const ReferenceContactValue: React.FC<ReferenceContactValueProps> = props => {
    const {
        referenceContactId,
        referenceBusinessId,
        communityId,
        parentEntityType,
        parentId,
    } = props;

    const referenceContactQuery = useGetReferenceContactQuery({ id: referenceContactId });

    return (
        <QueryRenderer
            query={referenceContactQuery}
            name="ReferenceContactValue.getReferenceContact">
            {data => {
                if (data.getReferenceContact.deleted) {
                    return (
                        <InlineBanner type="warning">
                            <p className="text-sm">
                                This referral contact has been deleted and its information
                                is no longer available
                            </p>
                        </InlineBanner>
                    );
                }

                const contact = Contact.load(data.getReferenceContact.contactEntity);
                const contactChangedOrganizations =
                    (!!referenceBusinessId &&
                        data.getReferenceContact.business?.id !== referenceBusinessId) ||
                    (!!communityId &&
                        data.getReferenceContact.community?.id !== communityId);

                const newOrganizationName =
                    data.getReferenceContact.business?.businessContactEntity.name ||
                    data.getReferenceContact.community?.name;

                return (
                    <>
                        {contactChangedOrganizations ? (
                            <InlineBanner type="info">
                                <p className="text-sm">
                                    {data.getReferenceContact.contactEntity.name} is now
                                    with {newOrganizationName || "another organization"}
                                </p>
                            </InlineBanner>
                        ) : null}
                        <div className={`${wrapperStyle} md:flex md:space-x-4`}>
                            <div className="pb-4 md:flex-1 md:min-w-0">
                                <ContactCard
                                    parentEntityType={parentEntityType}
                                    parentId={parentId}
                                    contact={contact}
                                    href={
                                        data.getReferenceContact.deleted
                                            ? null
                                            : `/references/contacts/show/${referenceContactId}`
                                    }
                                    icon="40_ReferralPerson"
                                />
                                <AssignedTo userId={data.getReferenceContact.ownerId} />
                            </div>
                            {referenceBusinessId ? (
                                <div className="md:flex-1 md:min-w-0">
                                    <ReferenceBusinessValue
                                        referenceId={referenceBusinessId}
                                        parentEntityType={parentEntityType}
                                        parentId={parentId}
                                    />
                                </div>
                            ) : null}
                            {communityId ? (
                                <div className="md:flex-1 md:min-w-0">
                                    <ReferenceCommunityValue
                                        communityId={communityId}
                                        parentEntityType={parentEntityType}
                                        parentId={parentId}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </>
                );
            }}
        </QueryRenderer>
    );
};

interface ReferenceBusinessValueProps {
    referenceId: ReferenceBusinessId;
    parentEntityType: AdvancedSearchEntityType;
    parentId: AnswerEntityId;
}

const ReferenceBusinessValue: React.FC<ReferenceBusinessValueProps> = props => {
    const { referenceId, parentEntityType, parentId } = props;

    const query = useGetReferenceBusinessQuery({ id: referenceId });

    return (
        <QueryRenderer query={query} name="ReferenceContactValue.getReferenceBusiness">
            {data => {
                if (data.getReferenceBusiness.deleted) {
                    return (
                        <InlineBanner type="warning">
                            <p className="text-sm">
                                This referral organization has been deleted and its
                                information is no longer available
                            </p>
                        </InlineBanner>
                    );
                }

                return (
                    <>
                        <ContactCard
                            parentEntityType={parentEntityType}
                            parentId={parentId}
                            contact={Contact.load(
                                data.getReferenceBusiness.businessContactEntity,
                            )}
                            href={
                                data.getReferenceBusiness.deleted
                                    ? null
                                    : `/references/organizations/show/${referenceId}`
                            }
                            icon="39_Business"
                        />
                        <AssignedTo userId={data.getReferenceBusiness.ownerId} />
                    </>
                );
            }}
        </QueryRenderer>
    );
};

interface ReferenceCommunityValueProps {
    communityId: CommunityId;
    parentEntityType: AdvancedSearchEntityType;
    parentId: AnswerEntityId;
}

const ReferenceCommunityValue: React.FC<ReferenceCommunityValueProps> = props => {
    const { communityId, parentEntityType, parentId } = props;

    const query = useGetCommunityQuery({ id: communityId });

    return (
        <QueryRenderer query={query} name="ReferenceContactValue.getCommunity">
            {data => {
                const communityAsContact: IContact = Contact.load({
                    id: parseEntityId<ContactId>(data.getCommunity.id),
                    name: data.getCommunity.name,
                    address1: data.getCommunity.address,
                    city: data.getCommunity.city,
                    state: data.getCommunity.state,
                    zip: data.getCommunity.zip,
                    phone1: data.getCommunity.mainPhone,
                    website: data.getCommunity.website,
                    email1: data.getCommunity.email,
                });

                return (
                    <ContactCard
                        parentEntityType={parentEntityType}
                        parentId={parentId}
                        contact={communityAsContact}
                        href={`/communities/show/${communityId}`}
                        icon="57_Community"
                    />
                );
            }}
        </QueryRenderer>
    );
};

interface ReferralSourcePickerValueProps {
    value: ReferenceMap;
    parentEntityType: AdvancedSearchEntityType;
    parentId: AnswerEntityId;
}

const ReferralSourcePickerValue: React.FC<ReferralSourcePickerValueProps> = props => {
    const { value, parentEntityType, parentId } = props;

    const identifier = value.identifier.getOrElse("").trim() || null;
    const referenceContactId = value.contact.getOrElse("").trim() || null;
    const referenceBusinessId = value.business.getOrElse("").trim() || null;

    if (identifier) {
        if (identifier === COMMUNITY_REFERRAL) {
            if (referenceContactId) {
                return (
                    <ReferenceContactValue
                        referenceContactId={referenceContactId as ReferenceContactId}
                        referenceBusinessId={null}
                        communityId={referenceBusinessId as CommunityId}
                        parentEntityType={parentEntityType}
                        parentId={parentId}
                    />
                );
            }

            return (
                <div className={wrapperStyle}>
                    <ReferenceCommunityValue
                        communityId={referenceBusinessId as CommunityId}
                        parentEntityType={parentEntityType}
                        parentId={parentId}
                    />
                </div>
            );
        }
        return (
            <div>
                {DefaultReferences.find(r => r.key === identifier)?.text || identifier}
            </div>
        );
    }

    if (referenceContactId) {
        return (
            <ReferenceContactValue
                referenceContactId={referenceContactId as ReferenceContactId}
                referenceBusinessId={referenceBusinessId as ReferenceBusinessId}
                communityId={null}
                parentEntityType={parentEntityType}
                parentId={parentId}
            />
        );
    }

    if (referenceBusinessId) {
        return (
            <div className={wrapperStyle}>
                <ReferenceBusinessValue
                    referenceId={referenceBusinessId as ReferenceBusinessId}
                    parentEntityType={parentEntityType}
                    parentId={parentId}
                />
            </div>
        );
    }

    return null;
};

interface ReferralSourceSummaryProps {
    name: string;
    kind: string;
}

const ReferralSourceSummary: React.FC<ReferralSourceSummaryProps> = props => {
    const { name, kind } = props;

    return (
        <div>
            <div className="text-sm text-gray-900">{name}</div>
            <div className="text-xs text-gray-500">{kind}</div>
        </div>
    );
};

const ReferenceBusinessSummary: React.FC<{
    referenceId: ReferenceBusinessId;
}> = props => {
    const query = useGetReferenceBusinessQuery({ id: props.referenceId });

    return (
        <QueryRenderer query={query} name="ReferralSourceSummary.getReferenceBusiness">
            {data => (
                <ReferralSourceSummary
                    name={
                        data.getReferenceBusiness.businessContactEntity.name ||
                        "(unnamed)"
                    }
                    kind="Referral organization"
                />
            )}
        </QueryRenderer>
    );
};

const ReferenceContactSummary: React.FC<{
    referenceContactId: ReferenceContactId;
}> = props => {
    const query = useGetReferenceContactQuery({ id: props.referenceContactId });

    return (
        <QueryRenderer query={query} name="ReferralSourceSummary.getReferenceContact">
            {data => (
                <ReferralSourceSummary
                    name={data.getReferenceContact.contactEntity.name || "(unnamed)"}
                    kind="Referral contact"
                />
            )}
        </QueryRenderer>
    );
};

const ReferenceCommunitySummary: React.FC<{ communityId: CommunityId }> = props => {
    const query = useGetCommunityQuery({ id: props.communityId });

    return (
        <QueryRenderer query={query} name="ReferralSourceSummary.getCommunity">
            {data => (
                <ReferralSourceSummary
                    name={data.getCommunity.name || "(unnamed)"}
                    kind="Community"
                />
            )}
        </QueryRenderer>
    );
};

const ReferralSourcePickerSummary: React.FC<{ value: ReferenceMap }> = props => {
    const { value } = props;

    const identifier = value.identifier.getOrElse("").trim() || null;
    const referenceContactId = value.contact.getOrElse("").trim() || null;
    const referenceBusinessId = value.business.getOrElse("").trim() || null;

    if (identifier && identifier !== COMMUNITY_REFERRAL) {
        return (
            <ReferralSourceSummary
                name={
                    DefaultReferences.find(r => r.key === identifier)?.text || identifier
                }
                kind="General referral source"
            />
        );
    }
    if (identifier === COMMUNITY_REFERRAL && referenceBusinessId) {
        return (
            <ReferenceCommunitySummary communityId={referenceBusinessId as CommunityId} />
        );
    }
    if (referenceContactId) {
        return (
            <ReferenceContactSummary
                referenceContactId={referenceContactId as ReferenceContactId}
            />
        );
    }
    if (referenceBusinessId) {
        return (
            <ReferenceBusinessSummary
                referenceId={referenceBusinessId as ReferenceBusinessId}
            />
        );
    }
    return null;
};

export type ReferralSourceCreateOption = "contact" | "organization";

type ReferralSourcePickerMode =
    | { kind: "search" }
    | { kind: "newContact"; referenceContactId: ReferenceContactId | null }
    | { kind: "newOrganization"; referenceBusinessId: ReferenceBusinessId | null };

export type ReferralSourcePickerVariant =
    | {
          kind: "detailed";
          parentEntityType: AdvancedSearchEntityType;
          parentId: AnswerEntityId;
      }
    | { kind: "compact" };
export type ReferralSourceClearing = "allowed" | "disallowed";

interface ReferralSourcePickerProps {
    value: ReferenceMap;
    onChange: (value: ReferenceMap) => void;
    createOptions: ReferralSourceCreateOption[];
    variant: ReferralSourcePickerVariant;
    clearing: ReferralSourceClearing;
    exclude?: ReferralSourceSearchTab[];
    label?: string;
    showIcon?: boolean;
}

const panelHeaderFor = (mode: ReferralSourcePickerMode): string => {
    switch (mode.kind) {
        case "newContact":
            return "New Referral Source";
        case "newOrganization":
            return "New Referral Organization";
        case "search":
            return "Choose Referral Source";
        default: {
            const exhaustiveCheck: never = mode;
            throw new Error(`Unhandled picker mode: ${exhaustiveCheck}`);
        }
    }
};

const isReferenceMapPopulated = (value: ReferenceMap): boolean => {
    const identifier = value.identifier.getOrElse("").trim() || null;
    const referenceContactId = value.contact.getOrElse("").trim() || null;
    const referenceBusinessId = value.business.getOrElse("").trim() || null;

    return !!identifier || !!referenceContactId || !!referenceBusinessId;
};

export const ReferralSourcePicker: React.FC<ReferralSourcePickerProps> = props => {
    const { value, onChange, createOptions, variant, clearing, exclude, label } = props;

    const [showModal, setShowModal] = useState<boolean>(false);
    const [mode, setMode] = useState<ReferralSourcePickerMode>({ kind: "search" });
    const client = useQueryClient();

    const handleChoose = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            setShowModal(true);
        },
        [setShowModal],
    );

    const clearReferralSource = useCallback(() => {
        onChange({
            identifier: Maybe.none(),
            contact: Maybe.none(),
            business: Maybe.none(),
        });
    }, [onChange]);

    const hide = useCallback(() => {
        client.invalidateQueries("getReferenceContact");
        client.invalidateQueries("getReferenceBusiness");
        setMode({ kind: "search" });
        setShowModal(false);
    }, [setShowModal, client]);

    const handleSearchSelect = useCallback(
        (selected: ReferenceMap) => {
            hide();
            onChange(selected);
        },
        [onChange, hide],
    );
    const showIcon = props.showIcon ?? true;

    const createReferenceContactMutation = useCreateReferenceContactMutation();
    const createReferenceBusinessMutation = useCreateReferenceBusinessMutation();
    const regionId = useRegionId();

    const createNewReferralContact = useCallback(async () => {
        setMode({ kind: "newContact", referenceContactId: null });
        const response = await createReferenceContactMutation.mutateAsync({
            regionId,
        });
        if (response?.createReferenceContact?.id) {
            const referenceContactId = response.createReferenceContact.id;
            setMode({ kind: "newContact", referenceContactId });
            onChange({
                identifier: Maybe.none(),
                business: Maybe.none(),
                contact: Maybe.fromValue(referenceContactId),
            });
        }
    }, [regionId, createReferenceContactMutation, onChange]);

    const createNewReferralOrganization = useCallback(async () => {
        setMode({ kind: "newOrganization", referenceBusinessId: null });
        const response = await createReferenceBusinessMutation.mutateAsync({
            regionId,
        });
        if (response?.createReferenceBusiness?.id) {
            const referenceBusinessId = response.createReferenceBusiness.id;
            setMode({ kind: "newOrganization", referenceBusinessId });
            onChange({
                identifier: Maybe.none(),
                business: Maybe.fromValue(referenceBusinessId),
                contact: Maybe.none(),
            });
        }
    }, [regionId, createReferenceBusinessMutation, onChange]);

    return (
        <>
            <div className={variant.kind === "compact" ? "space-y-2" : "space-y-4"}>
                <div
                    className={
                        variant.kind === "compact"
                            ? "flex items-center space-x-2"
                            : "flex items-center justify-between"
                    }>
                    <div className="flex items-center space-x-2">
                        {showIcon ? (
                            <div className="w-7">
                                <Icon name="38_ReferralSource" />
                            </div>
                        ) : null}
                        <span
                            className={
                                variant.kind === "compact"
                                    ? "text-gray-700"
                                    : "text-lg text-gray-700"
                            }>
                            {label ?? "Referral Source"}
                        </span>
                    </div>
                    <div className="space-x-2 flex items-center">
                        {clearing === "allowed" && isReferenceMapPopulated(value) ? (
                            <DeleteButton
                                backgroundColor="bg-white"
                                confirm={{
                                    message: "Remove this referral source?",
                                    title: "Clear referral source?",
                                    confirmLabel: "Yes, clear",
                                }}
                                onClick={clearReferralSource}
                            />
                        ) : null}
                        {variant.kind === "compact" ? (
                            <SecondaryCompactButton onClick={handleChoose}>
                                Choose
                            </SecondaryCompactButton>
                        ) : (
                            <SecondaryButton onClick={handleChoose}>
                                Choose
                            </SecondaryButton>
                        )}
                    </div>
                </div>
                {variant.kind === "compact" ? (
                    <ReferralSourcePickerSummary value={value} />
                ) : (
                    <ReferralSourcePickerValue
                        value={value}
                        parentEntityType={variant.parentEntityType}
                        parentId={variant.parentId}
                    />
                )}
            </div>
            <Panel
                isOpen={showModal}
                headerText={panelHeaderFor(mode)}
                type={PanelType.extraLarge}
                onDismiss={hide}>
                {mode.kind === "newContact" ? (
                    <NewInlineReference
                        referenceContactId={mode.referenceContactId}
                        dismiss={hide}
                    />
                ) : mode.kind === "newOrganization" ? (
                    <NewInlineOrganization
                        referenceBusinessId={mode.referenceBusinessId}
                        dismiss={hide}
                    />
                ) : (
                    <div className="space-y-1 lg:space-y-2">
                        <div className="flex justify-end space-x-2">
                            {createOptions.includes("organization") ? (
                                <PrimaryButton onClick={createNewReferralOrganization}>
                                    Create new referral organization
                                </PrimaryButton>
                            ) : null}
                            {createOptions.includes("contact") ? (
                                <PrimaryButton onClick={createNewReferralContact}>
                                    Create new referral contact
                                </PrimaryButton>
                            ) : null}
                        </div>
                        <ReferralSourceSearch
                            onSelect={handleSearchSelect}
                            exclude={exclude}
                        />
                    </div>
                )}
            </Panel>
        </>
    );
};
